import React, {Component} from 'react';
import logotipo from '../../images/logotipo_dashbo_purple.svg';
import LoginButton from './LoginButton';
import {TermsAndConditions} from "./TermsAndConditions";
import {LoginCover} from "./LoginCover";
import {FaIcon} from "../FaIcon";
import { withTranslation } from 'react-i18next';
import {APIClient} from "../../utils/APIClient";
import {LocalStorage} from "../../utils/LocalStorage";
import {Navigate} from "react-router-dom";
import {API_URL} from "../../settings/local_settings";
import {Spinner} from "../Spinner";
import {markLeadTokenFresh} from "../agency/leadPrefill";
import {CLARITY_UNMASK} from '../../utils/clarity/unmask';

class LoginPageComponent extends Component {
    render() {
        return (
            <div className="app-content">
                <LoginSidebar/>
                <LoginCover/>
            </div>
        );
    }
}

class SignUpPageComponent extends Component {
    componentDidMount() {
        // El sitio de marketing llega acá con ?token=<lead>. Se guarda antes del login con
        // Google, que es un redirect de página completa y se lleva puesto el querystring.
        // Lo consume el alta de agencia para saber de qué campaña vino.
        const token = new URLSearchParams(window.location.search).get('token');
        LocalStorage.saveSignupLeadToken(token);
        // Y se marca que llegó EN ESTA PESTAÑA: sólo así «Crea tu agencia» lo usa para prellenar
        // (ver `isLeadTokenFresh`). La marca va sólo si lo guardado es este mismo token: si el de la
        // URL no es válido, el getter devolvería uno viejo, y ése no puede quedar como recién llegado.
        const saved = LocalStorage.getSignupLeadToken();
        if (token && saved === String(token).trim()) {
            markLeadTokenFresh(saved);
        }
        // El código de prueba del Cyber (CY-T): `/signup?codigo=CYBER30` lo deja guardado y el alta lo
        // aplica sola al crear la cuenta. Sale de la URL con el token, por lo mismo.
        const trialCode = new URLSearchParams(window.location.search).get('codigo');
        LocalStorage.saveSignupTrialCode(trialCode);
        // Guardado, el token sale de la URL: con él (y cualquier cuenta de Google sin agencia),
        // `agency-lead/prefill/` devuelve los datos del lead, y Clarity graba la URL entera de cada
        // página (UD-7). Recargar no lo pierde: un `/signup` sin token no borra lo guardado, y la
        // marca de «llegó en esta pestaña» vive en `sessionStorage`. Va por fuera del router, que no
        // necesita enterarse: corre antes que el `useEffect` que decide si se graba.
        if (token !== null || trialCode !== null) {
            const params = new URLSearchParams(window.location.search);
            params.delete('token');
            params.delete('codigo');
            const query = params.toString();
            window.history.replaceState(window.history.state, '',
                window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
        }
    }

    render() {
        return (
            <div className="app-content sign-up">
                <LoginSidebar isSignUp/>
                <LoginCover/>
            </div>
        );
    }
}

/**
 * La columna del login, con el lenguaje de la 2.1 (15/10, propuesta A de la maqueta «Login de Dashbo
 * 2.1»): un solo logo, el título dice qué se hace acá y el botón de Google ocupa el ancho de la
 * columna.
 *
 * /login y /signup son la misma columna con otro texto. En /signup el subtítulo dice para qué es todo
 * esto antes del primer clic, a quien llega desde un anuncio: nombra las dos cosas —el seguimiento y
 * la IA— porque no todos entran por la IA (copy del 30/09, card VELQYFFQ; Leo lo había leído «muy
 * acotado»). En /login va la línea para quien entra por primera vez: el mismo botón de Google crea la
 * agencia (`LoginButton` manda a «Crea tu agencia» a quien no tiene una), y la pantalla no lo decía.
 */
class LoginSidebarComponent extends Component {
    render() {
        const { t, isSignUp } = this.props;
        const isReviewLogin = window.location.search.includes('review-login');

        return (
            <div className="login-sidebar">
                <img src={logotipo} className="login-logo" alt="Dashbo"/>
                <div className="login-main">
                    <div className="login-panel">
                        <h1 className="login-title" {...CLARITY_UNMASK}>{isSignUp ? t('login.signup_title') : t('login.title')}</h1>
                        <p className="login-lead" {...CLARITY_UNMASK}>{isSignUp ? t('login.signup_subtitle') : t('login.lead')}</p>
                        {isReviewLogin ? (
                            <PasswordLoginForm t={t}/>
                        ) : (
                            <LoginButton googleText={isSignUp ? 'continue_with' : undefined}/>
                        )}
                        {isSignUp ? (
                            <p className="login-trial" {...CLARITY_UNMASK}>
                                <FaIcon iconName="check" className="login-trial-icon"/>
                                {t('login.trial_note')}
                            </p>
                        ) : (
                            <p className="login-first-time">
                                <strong>{t('login.first_time_question')}</strong> {t('login.first_time_text')}
                            </p>
                        )}
                    </div>
                </div>
                {!isReviewLogin && <TermsAndConditions/>}
            </div>
        );
    }
}

class PasswordLoginFormComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            formData: {
                email: '',
                password: '',
            },
            saving: false,
            invalidCredentials: false,
            isLoggedIn: false
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
    }

    handleSubmit(event) {
        event.preventDefault();
        this.setState({saving: true, invalidCredentials: false});

        new APIClient(API_URL + 'login/password/').login({...this.state.formData}).then((response) => {
            this.setState({saving: false});
            if (response === 'UNAUTHORIZED') {
                this.setState({invalidCredentials: true});
            } else {
                const userData = response.data;
                LocalStorage.saveUserData(
                    userData.user_id,
                    userData.token,
                    userData.user_type,
                    userData.agency_status,
                    userData.language,
                    userData.client_id || null,
                    userData.is_dashbo_mcp,
                    userData.free_trial_days_remaining,
                    userData.agency_logo_url,
                    userData.agency_display_name,
                    userData.brand_primary_color,
                    userData.brand_secondary_color,
                    userData.portal_theme
                );
                LocalStorage.updateTrialNextStep(userData.trial_next_step);
                LocalStorage.saveUserClients(userData.clients || null);
                this.setState({isLoggedIn: true});
            }
        });
    }

    handleInputChange(event) {
        const target = event.target;
        const name = target.name;

        this.setState({
            formData: {
                ...this.state.formData,
                [name]: target.value
            }
        });
    }

    render() {
        const { t } = this.props;
        if (this.state.isLoggedIn) {
            return <Navigate to={{pathname: '/'}}/>
        }

        return (
            <form className="login-form" onSubmit={this.handleSubmit} method="post" style={{marginTop: '2em'}}>
                <div className="form-row">
                    <input name="email" type="email" className="form-control"
                           placeholder={t('login.email_placeholder')}
                           value={this.state.formData.email} onChange={this.handleInputChange}/>
                </div>
                <div className="form-row">
                    <input name="password" type="password" className="form-control"
                           placeholder={t('login.password_placeholder')}
                           value={this.state.formData.password} onChange={this.handleInputChange}/>
                </div>
                <div className="submit-row">
                    {this.state.saving ? (
                        <button className="btn -alt" type="button" disabled>
                            <Spinner color="negative small"/>
                        </button>
                    ) : (
                        <input type="submit" value={t('login.login_button')} className="btn -alt"/>
                    )}
                </div>
                {this.state.invalidCredentials && (
                    <p className="login-message -fail" style={{marginTop: '1em', color: '#DF0D00'}}>
                        {t('login.invalid_credentials')}
                    </p>
                )}
            </form>
        );
    }
}

const PasswordLoginForm = withTranslation()(PasswordLoginFormComponent);
export const LoginPage = withTranslation()(LoginPageComponent);
export const SignUpPage = withTranslation()(SignUpPageComponent);
export const LoginSidebar = withTranslation()(LoginSidebarComponent);
